import React from 'react';
import type { TableProps } from '@amzn/awsui-components-console/table';
import type { Collection } from '@amzn/aws-gcr-content-connection-management-client';
import { formatDate } from '../../utils/dateUtils';
import { cronToSyncFrequency, formatSyncFrequency } from '../../utils/cronUtils';

// Function to get item count color based on count ranges
const getItemCountColor = (count: number): string => {
  if (count < 1000) return '#1f77b4'; // blue
  if (count < 10000) return '#2ca02c'; // green
  if (count < 50000) return '#ff7f0e'; // orange
  if (count < 100000) return '#9467bd'; // purple
  return '#d62728'; // red for 100000+
};

// Data source colors matching home page overview
const getDataSourceColor = (source: string): string => {
  const colorMap: Record<string, string> = {
    Wiki: '#1f77b4',
    Highspot: '#ff7f0e',
    Quip: '#2ca02c',
    Web: '#d62728',
    JsonAPI: '#9467bd',
    BYOC: '#8c564b',
  };
  return colorMap[source] || '#8c564b';
};

export const getCollectionColumnDefinitions = (): TableProps.ColumnDefinition<Collection>[] => [
  {
    id: 'name',
    header: 'Name',
    cell: (e) =>
      React.createElement(
        'a',
        {
          href: e.collectionId,
          target: '_blank',
          rel: 'noopener noreferrer',
        },
        e.name,
      ),
    sortingField: 'name',
    isRowHeader: true,
    width: 180,
    minWidth: 150,
  },
  {
    id: 'description',
    header: 'Description',
    cell: (e) => e.description,
    sortingField: 'description',
    width: 350,
    minWidth: 250,
  },
  {
    id: 'dataSource',
    header: 'Data source',
    cell: (e) => <span style={{ color: getDataSourceColor(e.source || ''), fontWeight: 'bold' }}>{e.source}</span>,
    sortingField: 'source',
    width: 150,
  },
  {
    id: 'itemCount',
    header: 'Item #',
    cell: (e) => {
      const count = e.itemCount || 0;
      return <span style={{ color: getItemCountColor(count), fontWeight: 'bold' }}>{count.toLocaleString()}</span>;
    },
    sortingField: 'itemCount',
    width: 120,
  },
  {
    id: 'applicationCount',
    header: 'Application #',
    cell: (e) => (e.useCases || []).length.toLocaleString(),
    sortingField: 'useCases',
    width: 150,
  },
  {
    id: 'creator',
    header: 'Creator',
    cell: (e) => e.createdBy,
    sortingField: 'createdBy',
    width: 120,
  },
  {
    id: 'createTime',
    header: 'Create Time',
    cell: (e) => formatDate(e.createdAt || ''),
    sortingField: 'createdAt',
    width: 210,
  },
  {
    id: 'lastUpdateTime',
    header: 'Last Update Time',
    cell: (e) => formatDate(e.lastUpdatedAt || ''),
    sortingField: 'lastUpdatedAt',
    width: 210,
  },
  {
    id: 'lastUpdatedBy',
    header: 'Last Updated By',
    cell: (e) => e.lastUpdatedBy,
    sortingField: 'lastUpdatedBy',
    width: 150,
  },
  {
    id: 'syncFrequency',
    header: 'Sync Frequency',
    cell: (e) => formatSyncFrequency(cronToSyncFrequency(e.schedule || '')),
    sortingField: 'schedule',
    minWidth: 200,
    width: 300,
  },
];

export const filterCollections = (collections: Collection[], filteringText: string, selectedDataSource: string | null): Collection[] => {
  return collections.filter((item) => {
    const matchesText =
      (item.name || '').toLowerCase().includes(filteringText.toLowerCase()) ||
      (item.description || '').toLowerCase().includes(filteringText.toLowerCase()) ||
      (item.source || '').toLowerCase().includes(filteringText.toLowerCase()) ||
      (item.createdBy || '').toLowerCase().includes(filteringText.toLowerCase());

    const matchesDataSource = !selectedDataSource || item.source === selectedDataSource;

    return matchesText && matchesDataSource;
  });
};

export const sortCollections = (collections: Collection[], sortingField: string, isDescending: boolean): Collection[] => {
  return [...collections].sort((a, b) => {
    const field = sortingField as keyof Collection;
    let aValue = a[field];
    let bValue = b[field];

    // Handle date fields
    if (field === 'createdAt' || field === 'lastUpdatedAt') {
      aValue = new Date(aValue as string).getTime() as unknown as Collection[typeof field];
      bValue = new Date(bValue as string).getTime() as unknown as Collection[typeof field];
    }

    // Handle numeric fields
    // Handle special sorting for useCases (application count)
    if (field === 'itemCount' || sortingField === 'useCases') {
      aValue = Number(aValue);
      bValue = Number(bValue);
    }

    // Handle string fields
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      const comparison = aValue.localeCompare(bValue);
      return isDescending ? -comparison : comparison;
    }

    // Handle numeric comparison
    if ((aValue || 0) < (bValue || 0)) return isDescending ? 1 : -1;
    if ((aValue || 0) > (bValue || 0)) return isDescending ? -1 : 1;
    return 0;
  });
};
